import React from "react";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import { ICertificate } from "../../../types";
import { makeStyles, createStyles, Theme } from "@material-ui/core";
import moment from "moment";

// @ts-ignore
import styled from "styled-components/macro";
import { getDosages } from "../../../helpers";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "& > *": {
        width: "100%",
      },
    },
    cardHeader: {
      margin: "10px",
    },
    margin: {
      margin: theme.spacing(1),
    },
    bullet: {
      display: "inline-block",
      margin: "0 2px",
      transform: "scale(0.8)",
    },
    button: {
      margin: theme.spacing(1),
    },
    title: {
      fontSize: 14,
    },
    label: {
      marginBottom: 1,
      fontSize: "15px",
    },
    content: {
      marginBottom: 10,
      marginRight: 10,
      fontSize: "15px",
    },
    cardContent: {
      display: "flex",
      flexDirection: "column",
    },
    headerLabel: {
      marginLeft: "10px",
      fontSize: "15px",
    },
  })
);

const CertificateDemographic: React.FC<{ certificate: ICertificate }> = ({
  certificate,
}) => {
  const classes = useStyles();
  const { boosterShots, dosages } = getDosages(certificate.dosageIndicators);

  return (
    <>
      <DemographicsContainer>
        <p>Name</p>
        <h3>{`${certificate.firstname} ${certificate.lastname}`}</h3>
      </DemographicsContainer>
      <DemographicsContainer>
        <p>Date of Birth</p>
        <h3>{certificate.birthdate}</h3>
      </DemographicsContainer>
      <hr />

      <DosagesContainer>
        <MainDoseContainer>
          {dosages.map((dosage) => (
            <DosageBox key={dosage.dosageNumber}>
              <Card className={classes.root} variant="outlined">
                <Typography
                  className={classes.cardHeader}
                  variant="h5"
                  component="h2"
                >
                  {`DOSE ${dosage.dosageNumber}`}
                </Typography>
                <Typography
                  className={classes.headerLabel}
                  color="textSecondary"
                >
                  {`vaccination date: ${moment(dosage.vaccinationDate).format(
                    "DD-MM-YYYY"
                  )}`}
                </Typography>
                <hr />
                <CardContent className={classes.cardContent}>
                  <CardContentAttr>
                    <Attribute>
                      <Typography
                        className={classes.label}
                        color="textSecondary"
                      >
                        {"Name:"}
                      </Typography>
                      <Typography className={classes.content}>
                        {dosage.vaccineName}
                      </Typography>
                    </Attribute>
                    {dosage.type ? (
                      <Attribute>
                        <Typography
                          className={classes.label}
                          color="textSecondary"
                        >
                          {"Type:"}
                        </Typography>
                        <Typography className={classes.label}>
                          {dosage.type}
                        </Typography>
                      </Attribute>
                    ) : null}
                  </CardContentAttr>
                  <CardContentAttr>
                    <Attribute>
                      <Typography
                        className={classes.label}
                        color="textSecondary"
                      >
                        {"Batch:"}
                      </Typography>
                      <Typography className={classes.content}>
                        {dosage.batchNumber || "  ----"}
                      </Typography>
                    </Attribute>
                    <Attribute>
                      <Typography
                        className={classes.label}
                        color="textSecondary"
                      >
                        {"site:"}
                      </Typography>
                      <Typography className={classes.label}>
                        {dosage.vaccinationSite || "  ----"}
                      </Typography>
                    </Attribute>
                  </CardContentAttr>
                  <CardContentAttr>
                    <Attribute>
                      <Typography
                        className={classes.label}
                        color="textSecondary"
                      >
                        {"Batch Expiration Date:"}
                      </Typography>
                      <Typography className={classes.content}>
                        {dosage.expirationDate || "  ----"}
                      </Typography>
                    </Attribute>
                  </CardContentAttr>
                </CardContent>
              </Card>
            </DosageBox>
          ))}
        </MainDoseContainer>
        {boosterShots.length > 0
          ? boosterShots.map((shot, index) => {
              return (
                <Card
                  className={classes.root}
                  style={{ marginTop: "20px" }}
                  variant="outlined"
                  key={index}
                >
                  <BoosterHeader>
                    <Typography
                      className={classes.cardHeader}
                      style={{ margin: 0 }}
                      variant="h5"
                      component="h2"
                    >
                      Booster Shot
                    </Typography>
                    <BoosterHeaderContent>
                      <Typography
                        className={classes.headerLabel}
                        color="textSecondary"
                      >
                        site:
                        {shot.vaccinationSite}
                      </Typography>
                      <Typography
                        className={classes.headerLabel}
                        color="textSecondary"
                      >
                        {`date: ${moment(shot.vaccinationDate).format(
                          "DD-MM-YYYY"
                        )}`}
                      </Typography>
                    </BoosterHeaderContent>
                  </BoosterHeader>

                  <BoosterContent key={index}>
                    <Attribute>
                      <Typography
                        className={classes.label}
                        color="textSecondary"
                      >
                        {"Name:"}
                      </Typography>
                      <Typography className={classes.content}>
                        {shot.vaccineName}
                      </Typography>
                    </Attribute>
                    <Attribute>
                      <Typography
                        className={classes.label}
                        color="textSecondary"
                      >
                        {"Type:"}
                      </Typography>
                      <Typography className={classes.content}>
                        {shot.type}
                      </Typography>
                    </Attribute>
                    <Attribute>
                      <Typography
                        className={classes.label}
                        color="textSecondary"
                      >
                        {"Batch Number:"}
                      </Typography>
                      <Typography className={classes.content}>
                        {shot.batchNumber}
                      </Typography>
                    </Attribute>
                    <Attribute>
                      <Typography
                        className={classes.label}
                        color="textSecondary"
                      >
                        {"Expiration:"}
                      </Typography>
                      <Typography className={classes.content}>
                        {shot.expirationDate}
                      </Typography>
                    </Attribute>
                  </BoosterContent>
                </Card>
              );
            })
          : null}
      </DosagesContainer>
    </>
  );
};

const DemographicsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  & > p {
    margin-bottom: 0px;
    color: rgba(0, 0, 0, 0.54);
    line-height: 1.5;
    letter-spacing: 0.00938em;
  }
  & > h3 {
    margin-top: 0px;
  }
`;

const DosageBox = styled.div`
  margin: 1px;
  @media (max-width: 960px) {
    margin-bottom: 20px;
    min-width: 300px;
  }
`;

const DosagesContainer = styled.div`
  display: flex;
  margin-top: 10px;
  flex-direction: column;
  @media (max-width: 960px) {
    width: 100%;
  }
`;

const CardContentAttr = styled.div`
  display: flex;
  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

const Attribute = styled.div`
  display: flex;
`;

const BoosterHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: rgba(27, 105, 43, 0.16);
  color: rgba(8, 33, 8, 1);
`;
const MainDoseContainer = styled.div`
  display: flex;
  flex-direction: "row";
  @media (max-width: 960px) {
    flex-direction: column;
    width: 100%;
  }
`;

const BoosterContent = styled.div`
  display: flex;
  padding-top: 5px;
  padding-left: 5px;
  justify-content: center;
  align-items: center;
  @media (max-width: 960px) {
    flex-direction: column;
    width: 100%;
  }
`;

const BoosterHeaderContent = styled.div`
  display: flex;
`;

export default CertificateDemographic;
